/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import autobind from 'class-autobind';
import Leaflet, { Map as LeafletMap, Marker as LeafletMarker } from 'leaflet';
import MapIcon from './LeafletMapIcon';
import toElement from '../../../support/helpers/toElement';

import { FLUSH_VIEWSTATE_DELAY_MS } from './Constants';

type MapMarker = {
  map: LeafletMap;
  marker: LeafletMarker;
};
type MarkerDataSet = Map<HTMLElement, MapMarker>;

export type MarkerModifier = {
  update: (el: ReactElement) => void;
  destroy: () => void;
};

type Props = {
  children?: React.ReactNode;
  itemKey: string;
  lat: number;
  lng: number;
  onClick?: (...args: Array<any>) => any;
  onMove?: ((...args: Array<any>) => any) | null | undefined;
  percentage?: number;
};

const markerIcon = new MapIcon();
const markerData: MarkerDataSet = new Map();

let markerBackgroundImage = (percentage?: number) => {
  let strokeColor = '#fff';
  let normalizedPercentage =
    percentage != null ? (percentage > 100 ? 100 : percentage) : 100;
  if (normalizedPercentage >= 100) {
    strokeColor = '#7aba4c';
  } else {
    strokeColor = '#ffd429';
  }
  return `url("data:image/svg+xml;base64,${window.btoa(`<svg viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="1.414">
      <circle cx="50%" cy="50%" r="15.497" fill-opacity=".702" stroke="#b3bfcb" stroke-width="1"/>
      <circle cx="50%" cy="50%" r="15.497" fill-opacity=".702" stroke="${strokeColor}" stroke-dasharray="${normalizedPercentage}, 100" transform="rotate(-90, 17, 17)" stroke-width="${
    percentage ? '2' : '0'
  }"/>
    </svg>`)}")`;
};

export default class Marker extends Component<Props> {
  _map: LeafletMap | null | undefined;
  _marker: LeafletMarker | null | undefined;
  _ref = createRef<HTMLDivElement>();
  _refreshTimeout: TimeoutID;

  constructor(props: Props) {
    super(props);
    autobind(this);
  }

  static addToMap(reactEl: ReactElement, map: LeafletMap): MarkerModifier {
    let { props } = reactEl;
    let marker = Leaflet.marker([props.lat, props.lng], {
      icon: markerIcon,
      draggable: props.onMove != null,
    });
    marker.addTo(map);
    let element = marker.getElement();
    // this is a little hacky, but we need a way for the React instance to access map and marker
    markerData.set(element, { map, marker });
    ReactDOM.render(reactEl, element);
    return {
      update(newReactEl) {
        ReactDOM.render(newReactEl, element);
      },
      destroy() {
        ReactDOM.unmountComponentAtNode(element);
        markerData.delete(element);
        marker.remove();
      },
    };
  }

  componentDidMount() {
    let parentElement = this._ref.current
      ? toElement(this._ref.current.parentNode)
      : null;
    if (parentElement) {
      parentElement.style.backgroundImage = markerBackgroundImage(
        this.props.percentage,
      );
      let data = markerData.get(parentElement);
      if (data) {
        let { map, marker } = data;
        marker.on('dragstart', this._onDragStart);
        marker.on('dragend', this._onDragEnd);
        marker.on('click', this._onClick);
        this._map = map;
        this._marker = marker;
      }
    }
  }

  componentDidUpdate(oldProps: Props) {
    let marker = this._marker;
    if (marker == null) {
      return;
    }
    let newProps = this.props;
    let oldIsDraggable = oldProps.onMove != null;
    let newIsDraggable = newProps.onMove != null;
    if (oldIsDraggable !== newIsDraggable) {
      marker.options.draggable = newIsDraggable;
      if (newIsDraggable) {
        marker.dragging.enable();
      } else {
        marker.dragging.disable();
      }
    }
    if (
      oldProps.lat !== newProps.lat ||
      oldProps.lng !== newProps.lng ||
      oldProps.percentage !== newProps.percentage
    ) {
      this._refreshView(newProps);
    }
  }

  componentWillUnmount() {
    let marker = this._marker;
    if (marker == null) {
      return;
    }
    clearTimeout(this._refreshTimeout);
    marker.off('dragstart', this._onDragStart);
    marker.off('dragend', this._onDragEnd);
    marker.off('click', this._onClick);
  }

  render() {
    return <div ref={this._ref}>{this.props.children}</div>;
  }

  _refreshView(props?: Props | null | undefined) {
    let marker = this._marker;
    if (marker == null) {
      return;
    }
    let { lat, lng, percentage } = props || this.props;
    marker.setLatLng([lat, lng]);

    let parentElement = this._ref.current
      ? toElement(this._ref.current.parentNode)
      : null;
    if (parentElement) {
      parentElement.style.backgroundImage = markerBackgroundImage(percentage);
    }
  }

  _onClick() {
    if (this.props.onClick) {
      this.props.onClick();
    }
  }

  _onDragStart() {
    let marker = this._marker;
    if (marker != null) {
      let parentElement = this._ref.current
        ? toElement(this._ref.current.parentNode)
        : null;
      if (parentElement) {
        parentElement.classList.add('dragging');
      }
      marker.getElement().classList.add('dragging');
    }
  }

  _onDragEnd() {
    let marker = this._marker;
    if (marker == null) {
      return;
    }
    marker.getElement().classList.remove('dragging');
    if (!this.props.onMove) {
      return;
    }
    let { lat, lng } = marker.getLatLng();
    if (this.props.onMove) {
      this.props.onMove(lat, lng);
    }
    clearTimeout(this._refreshTimeout);
    // The marker's view state may now be out of sync with props. Refresh its view in a moment.
    this._refreshTimeout = setTimeout(() => {
      if (this.props.lat !== lat || this.props.lng !== lng) {
        this._refreshView();
      }
    }, FLUSH_VIEWSTATE_DELAY_MS);
  }
}
